import React, { useState, useEffect } from 'react';
import { getPayments } from '@/lib/api';
import { CreditCard, Loader2, Check, AlertTriangle, Clock, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const statusConfig = {
  paid: { bg: 'bg-[#E8EDE5]', text: 'text-[#3A4035]', icon: Check, label: 'Paid' },
  pending: { bg: 'bg-[#F4EFE6]', text: 'text-[#7A6849]', icon: Clock, label: 'Pending' },
  overdue: { bg: 'bg-[#F5E6E6]', text: 'text-[#8A3D3D]', icon: AlertTriangle, label: 'Overdue' },
};

export default function MyPayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    getPayments().then(setPayments).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-[#3A4035] animate-spin" /></div>;

  const totalPaid = payments.filter(p => p.status === 'paid').reduce((s, p) => s + p.amount, 0);

  return (
    <div className="animate-fade-in" data-testid="my-payments-page">
      <div className="mb-8">
        <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#1A1A1A] tracking-tight">Payments</h1>
        <p className="text-sm text-[#5C5C55] mt-1">Your payment history and upcoming dues</p>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-3 gap-3 mb-8">
        <div className="bg-[#E8EDE5] rounded-2xl p-4 text-center">
          <p className="text-xs font-semibold text-[#3A4035] mb-1">Paid</p>
          <p className="text-lg font-heading font-medium text-[#3A4035]">{payments.filter(p => p.status === 'paid').length}</p>
        </div>
        <div className="bg-[#F4EFE6] rounded-2xl p-4 text-center">
          <p className="text-xs font-semibold text-[#7A6849] mb-1">Pending</p>
          <p className="text-lg font-heading font-medium text-[#7A6849]">{payments.filter(p => p.status === 'pending').length}</p>
        </div>
        <div className="bg-[#F5E6E6] rounded-2xl p-4 text-center">
          <p className="text-xs font-semibold text-[#8A3D3D] mb-1">Overdue</p>
          <p className="text-lg font-heading font-medium text-[#8A3D3D]">{payments.filter(p => p.status === 'overdue').length}</p>
        </div>
      </div>

      <div className="space-y-2">
        {payments.map((pay, i) => {
          const cfg = statusConfig[pay.status] || statusConfig.pending;
          const Icon = cfg.icon;
          return (
            <div key={pay.payment_id}
              className={`bg-white rounded-2xl border border-[#E5E2DC] shadow-[0_2px_10px_rgba(0,0,0,0.02)] p-4 flex items-center gap-3 animate-fade-in stagger-${(i % 6) + 1}`}
              data-testid={`payment-item-${pay.payment_id}`}>
              <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${cfg.bg}`}>
                <Icon className={`w-4 h-4 ${cfg.text}`} strokeWidth={1.5} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-[#1A1A1A]">£{pay.amount?.toFixed(0)}</p>
                <p className="text-xs text-[#8A8B85]">Due {pay.due_date}{pay.paid_date ? ` · Paid ${pay.paid_date}` : ''}</p>
              </div>
              <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
            </div>
          );
        })}
      </div>

      {payments.length === 0 && (
        <div className="text-center py-16">
          <CreditCard className="w-10 h-10 text-[#D8DBD3] mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-sm text-[#8A8B85]">No payment records yet</p>
        </div>
      )}
    </div>
  );
}
